import type { DensityFiltersSettings, UserSettings } from "@screener/protocol";
import { CARD_ROW_ORDER_STORAGE_KEY, readCardRowOrder } from "@/density/card-row-order";
import type { CardRowOrder } from "@/density/cards";
import {
  DENSITY_FILTERS_STORAGE_KEY,
  isRecord,
  readDensityFilters,
  readExchanges,
  readFlag,
  readStates,
} from "@/density/density-filters-storage";
import { type DensityFilters, defaultFilters } from "@/density/derive";
import { readSidebarOpen, SIDEBAR_STORAGE_KEY } from "@/hooks/use-sidebar-open";

/**
 * Настройки аккаунта (§127): чистые функции разбора и сборки документа
 * `UserSettings`. Состояния и сети здесь нет — это `account-settings-store.ts`.
 *
 * Документ разбирается по разделам и поле за полем: нечитаемое поле берёт
 * значение по умолчанию, остальные применяются (design.md stage9-account-settings,
 * «Порча документа в базе»).
 */

/** Значения трёх настроек, как их видит экран. */
export interface AccountSettingsValue {
  filters: DensityFilters;
  rowOrder: CardRowOrder;
  sidebarOpen: boolean;
}

const DEFAULT_ROW_ORDER: CardRowOrder = "book";

export const defaultAccountSettings: AccountSettingsValue = {
  filters: defaultFilters,
  rowOrder: DEFAULT_ROW_ORDER,
  // Панель по умолчанию свёрнута в полосу значков (§88 п. 6).
  sidebarOpen: false,
};

/** Десятичная запись без знака и экспоненты — то, что демон разбирает как `Decimal`. */
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

/** Знаков после запятой, если число пришлось бы писать с экспонентой. */
const MIN_USD_FRACTION_DIGITS = 8;

/** `min_usd` документа (строка `Decimal`) → число экрана; нечитаемое — фильтр не применён. */
export function minUsdFromString(value: unknown): number | null {
  if (typeof value !== "string" || !DECIMAL_PATTERN.test(value)) {
    return defaultFilters.minUsd;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : defaultFilters.minUsd;
}

/** Число экрана → `min_usd` документа: строкой, без экспоненты. */
export function minUsdToString(value: number | null): string | null {
  if (value === null) {
    return null;
  }
  const plain = String(value);
  if (!plain.includes("e")) {
    return plain;
  }
  if (Math.abs(value) >= 1) {
    return BigInt(Math.trunc(value)).toString();
  }
  return value.toFixed(MIN_USD_FRACTION_DIGITS).replace(/0+$/, "");
}

/** Раздел фильтров документа → фильтры экрана; раздела нет или он не объект — по умолчанию. */
export function readFiltersSection(section: unknown): DensityFilters {
  if (!isRecord(section)) {
    return defaultFilters;
  }
  return {
    minUsd: minUsdFromString(section.min_usd),
    states: readStates(section.states),
    exchanges: readExchanges(section.exchanges),
    hideMm: readFlag(section.hide_mm, defaultFilters.hideMm),
    onlyFavorites: readFlag(section.only_favorites, defaultFilters.onlyFavorites),
  };
}

/** Фильтры экрана → раздел документа. */
export function filtersToSection(filters: DensityFilters): DensityFiltersSettings {
  return {
    min_usd: minUsdToString(filters.minUsd),
    states: filters.states,
    exchanges: filters.exchanges,
    hide_mm: filters.hideMm,
    only_favorites: filters.onlyFavorites,
  };
}

/** Неизвестный вид строк — «стакан». */
export function readRowOrderSection(section: unknown): CardRowOrder {
  return section === "book" || section === "volume" ? section : DEFAULT_ROW_ORDER;
}

export function readSidebarSection(section: unknown): boolean {
  return typeof section === "boolean" ? section : defaultAccountSettings.sidebarOpen;
}

/** Документ целиком → значения экрана. Не объект — всё по умолчанию. */
export function readAccountSettings(document: unknown): AccountSettingsValue {
  if (!isRecord(document)) {
    return defaultAccountSettings;
  }
  return {
    filters: readFiltersSection(document.density_filters),
    rowOrder: readRowOrderSection(document.card_row_order),
    sidebarOpen: readSidebarSection(document.sidebar_open),
  };
}

/** Документ в том виде, как пришёл: известные разделы типизированы, остальное — как есть. */
export type RawSettings = Record<string, unknown>;

const KNOWN_SECTIONS: readonly string[] = ["density_filters", "card_row_order", "sidebar_open"];
const KNOWN_FILTER_FIELDS: readonly string[] = Object.keys(filtersToSection(defaultFilters));

function isSet(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function pickUnknown(source: unknown, known: readonly string[]): RawSettings {
  const extra: RawSettings = {};
  if (isRecord(source)) {
    for (const [key, value] of Object.entries(source)) {
      if (!known.includes(key)) {
        extra[key] = value;
      }
    }
  }
  return extra;
}

/**
 * Раздел фильтров для записи: известные поля заново из разобранных значений,
 * неизвестные поля внутри раздела — как прочитаны (демон хранит и их).
 */
export function buildFiltersSection(
  previous: unknown,
  filters: DensityFilters,
): DensityFiltersSettings {
  return Object.assign({}, pickUnknown(previous, KNOWN_FILTER_FIELDS), filtersToSection(filters));
}

/**
 * Документ к записи: известные разделы собираются заново из разобранных значений
 * поле за полем, неизвестные поля верхнего уровня сохраняются как прочитаны.
 * Раздела, которого в документе нет, нет и в результате.
 */
export function buildSettingsPayload(document: RawSettings): UserSettings {
  const known: UserSettings = {};
  if (isSet(document.density_filters)) {
    known.density_filters = buildFiltersSection(
      document.density_filters,
      readFiltersSection(document.density_filters),
    );
  }
  if (isSet(document.card_row_order)) {
    known.card_row_order = readRowOrderSection(document.card_row_order);
  }
  if (isSet(document.sidebar_open)) {
    known.sidebar_open = readSidebarSection(document.sidebar_open);
  }
  return Object.assign({}, pickUnknown(document, KNOWN_SECTIONS), known);
}

/** Документ без разделов: писать его в аккаунт незачем. */
export function isEmptySettings(document: UserSettings): boolean {
  return (
    document.density_filters === undefined &&
    document.card_row_order === undefined &&
    document.sidebar_open === undefined
  );
}

/** Доступ к хранилищу функцией: при запрещённых данных сайта бросает уже сам геттер. */
export type StorageAccess = () => Storage | null;

/**
 * Документ для переезда (§127 п. 5): три прежних ключа браузера, прочитанные
 * прежними читателями. Раздел, ключа которого нет, не включается; ключ с
 * нечитаемым значением переезжает значением по умолчанию.
 */
export function collectLegacySettings(access: StorageAccess): UserSettings {
  try {
    const storage = access();
    if (storage === null) {
      return {};
    }
    const document: UserSettings = {};
    if (storage.getItem(DENSITY_FILTERS_STORAGE_KEY) !== null) {
      document.density_filters = filtersToSection(readDensityFilters(storage));
    }
    if (storage.getItem(CARD_ROW_ORDER_STORAGE_KEY) !== null) {
      document.card_row_order = readCardRowOrder(storage);
    }
    if (storage.getItem(SIDEBAR_STORAGE_KEY) !== null) {
      document.sidebar_open = readSidebarOpen(() => storage);
    }
    return document;
  } catch {
    return {};
  }
}

/** Убирает три прежних ключа: остаток в браузере после переезда никому не нужен. */
export function removeLegacySettings(access: StorageAccess): void {
  try {
    const storage = access();
    if (storage === null) {
      return;
    }
    storage.removeItem(DENSITY_FILTERS_STORAGE_KEY);
    storage.removeItem(CARD_ROW_ORDER_STORAGE_KEY);
    storage.removeItem(SIDEBAR_STORAGE_KEY);
  } catch {
    // Хранилище недоступно — убирать нечего.
  }
}
